/* eslint-disable camelcase */
import { Row, Col, Button } from 'antd';
import './index.css';
import { useMediaQuery } from '../../hooks/MediaQuery.hook';
import { getFixtureByName } from '../../apis/fixture.api';
import { useEffect, useRef, useState, useMemo, useCallback, memo } from 'react';
import Loader from '../../components/common/Loader';
import ConnectionBanner from '../../components/common/ConnectionBanner';
import { Link, useParams } from 'react-router-dom';
import TypographyTitle from '../../components/common/Title';
import Player from '../player';
import Title from 'antd/es/typography/Title';
import { styles } from '../../styles';
import { HOME_PATH, IMAGE_PROXY_URL, MENU_CATEGORIES, SITE_NAME, SITE_URL } from '../../utils/constant';
import { PlayCircleOutlined } from '@ant-design/icons';
import SportCategoriesStrip from '../../components/SportCategoriesStrip';
import JoinDiscord from '../../components/social/discord';
import {
  fixtureDetailCacheKey,
  isFixtureDetailResponse,
  readSessionCache,
  resilientFetch,
} from '../../utils/resilientFetch';
import {
  formatLocalMatchDate,
  getStaticMatchPhase,
  StaticMatchPhase,
} from '../../utils/timeDifference';
import { applyPageSeo, organizationSchema } from '../../utils/seo';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface Game {
  teamA: string;
  teamB: string;
  teamAImage: string;
  teamBImage: string;
  matchDate: string;
  matchTime: string;
  streamerLinks: StreamerLink[];
  websiteLink?: string;
}

interface StreamerLink {
  streamer: string;
  website: string;
  language: string;
  mobileSupport: string;
  websiteLink: string;
}

interface FixtureData {
  game: Game;
  subCategoryName: string;
}

// ---------------------------------------------------------------------------
// SEO helpers
// ---------------------------------------------------------------------------
function formatMatchLabel(teamA?: string, teamB?: string): string {
  if (!teamA) return 'Live Match';
  return teamB ? `${teamA} vs ${teamB}` : teamA;
}

function formatMatchDate(matchDate?: string): string {
  return formatLocalMatchDate(matchDate);
}

function uniqueValues(values: (string | undefined)[]): string[] {
  return Array.from(new Set(values.filter((v): v is string => Boolean(v))));
}

// ---------------------------------------------------------------------------
// Streams list (lighter than Ant Table for timer-isolated page)
// ---------------------------------------------------------------------------
const StreamsList = memo(function StreamsList({
  streams,
  isDesktop,
}: {
  streams: StreamerLink[];
  isDesktop: boolean;
}) {
  if (!streams.length) return null;

  return (
    <div className="streams-table streams-list">
      <div className={`streams-list-head ${isDesktop ? 'desktop' : 'mobile'}`}>
        <span>Streamer</span>
        <span>Channel</span>
        {isDesktop && <span>Language</span>}
        {isDesktop && <span>Cross-platform</span>}
        {isDesktop && <span>Quality</span>}
        <span>Link</span>
      </div>
      {streams.map((stream) => (
        <div
          className={`streams-list-row ${isDesktop ? 'desktop' : 'mobile'}`}
          key={`${stream.streamer}-${stream.website}-${stream.websiteLink}`}
        >
          <span>{stream.streamer}</span>
          <span>{stream.website}</span>
          {isDesktop && <span>{stream.language}</span>}
          {isDesktop && <span>{stream.mobileSupport}</span>}
          {isDesktop && <span>HD</span>}
          <span>
            <a href={stream.websiteLink} target="_blank" rel="noopener noreferrer">
              <Button
                type="link"
                style={{
                  borderRadius: 999,
                  border: '1px solid #1677ff',
                  padding: '0 14px',
                  fontWeight: 500,
                }}
                icon={<PlayCircleOutlined />}
              >
                Watch
              </Button>
            </a>
          </span>
        </div>
      ))}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Dynamic SEO description
// ---------------------------------------------------------------------------
const FixtureDescription = memo(function FixtureDescription({
  fixture,
  matchPhase,
  localKickOff,
}: {
  fixture: FixtureData;
  matchPhase: StaticMatchPhase;
  localKickOff: string;
}) {
  const { game, subCategoryName } = fixture;
  const matchLabel = formatMatchLabel(game.teamA, game.teamB);
  const league = subCategoryName || 'Sports';
  const scheduledAt = localKickOff || formatMatchDate(game.matchDate);
  const streamCount = game.streamerLinks?.length ?? 0;
  const streamers = uniqueValues(game.streamerLinks?.map((s) => s.streamer));
  const languages = uniqueValues(game.streamerLinks?.map((s) => s.language));
  const channels = uniqueValues(game.streamerLinks?.map((s) => s.website));
  const mobileFriendlyCount =
    game.streamerLinks?.filter((s) =>
      /yes|true|mobile|all/i.test(s.mobileSupport || ''),
    ).length ?? 0;

  const statusLabel =
    matchPhase === 'LIVE'
      ? 'LIVE'
      : matchPhase === 'FULL TIME'
        ? 'FULL TIME'
        : matchPhase === 'upcoming'
          ? 'Upcoming'
          : 'Scheduled';

  const statusCopy =
    matchPhase === 'LIVE'
      ? `${matchLabel} is LIVE now on Buffstreams. Switch between the streamer links above for the best free HD experience.`
      : matchPhase === 'FULL TIME'
        ? `${matchLabel} has finished. Check the Buffstreams homepage for more live fixtures and upcoming ${league} matches.`
        : `${matchLabel} is scheduled for ${scheduledAt || game.matchTime || 'soon'} (your local time). Free stream links usually appear about one hour before kick-off.`;

  return (
    <section className="fixture-seo" aria-label={`${matchLabel} live stream information`}>
      <Title level={2} className="fixture-seo-h2">
        Watch {matchLabel} Live Stream Free on Buffstreams
      </Title>

      <p className="fixture-seo-text">
        Catch every moment of <strong>{matchLabel}</strong>
        {game.teamB ? ` (${game.teamA} vs ${game.teamB})` : ''} in the{' '}
        <strong>{league}</strong> on Buffstreams. This fixture page brings you free HD
        live streams, real-time match status, and multiple streamer options so you never miss
        a goal, fight, or race highlight.
      </p>

      <p className="fixture-seo-text">{statusCopy}</p>

      <Title level={3} className="fixture-seo-h3">Match Details</Title>
      <ul className="fixture-seo-list">
        <li>
          <strong>Match:</strong> {matchLabel}
        </li>
        <li>
          <strong>Competition:</strong> {league}
        </li>
        {scheduledAt && (
          <li>
            <strong>Kick-off (local time):</strong> {scheduledAt}
          </li>
        )}
        <li>
          <strong>Status:</strong> {statusLabel}
        </li>
        <li>
          <strong>Available streams:</strong>{' '}
          {streamCount > 0
            ? `${streamCount} free stream link${streamCount === 1 ? '' : 's'}`
            : 'Links appear closer to kick-off'}
        </li>
      </ul>

      {streamCount > 0 && (
        <>
          <Title level={3} className="fixture-seo-h3">
            Free Stream Options for {matchLabel}
          </Title>
          <p className="fixture-seo-text">
            Buffstreams currently lists <strong>{streamCount}</strong> stream
            {streamCount === 1 ? '' : 's'} for this fixture
            {streamers.length > 0 && (
              <>
                {' '}from sources including <strong>{streamers.slice(0, 5).join(', ')}</strong>
              </>
            )}
            {channels.length > 0 && (
              <>
                {' '}on channels such as <strong>{channels.slice(0, 5).join(', ')}</strong>
              </>
            )}
            {languages.length > 0 && (
              <>
                . Available languages: <strong>{languages.join(', ')}</strong>
              </>
            )}
            . Many links support mobile and desktop viewing
            {mobileFriendlyCount > 0 && (
              <> ({mobileFriendlyCount} marked cross-platform)</>
            )}
            — pick any Watch button above to start streaming in HD.
          </p>
        </>
      )}

      <Title level={3} className="fixture-seo-h3">
        How to Watch {matchLabel} on Buffstreams
      </Title>
      <ol className="fixture-seo-steps">
        <li>Stay on this page for live {league} coverage of {matchLabel}.</li>
        <li>Use the player above or choose a streamer link from the table.</li>
        <li>If one stream buffers, switch to another free HD option instantly.</li>
        <li>
          Return to the{' '}
          <Link to={HOME_PATH}>Buffstreams homepage</Link> for more live fixtures today.
        </li>
      </ol>

      <p className="fixture-seo-text">
        Buffstreams is a free live sports streaming hub. Whether you follow soccer,
        NFL, NBA, UFC, boxing, F1, or other sports, we organise fixtures and streamer links
        so you can watch without subscriptions or registration.
      </p>

      <nav className="fixture-seo-nav" aria-label="More sports categories">
        <Title level={4} className="fixture-seo-h4">Explore More Free Streams</Title>
        <ul className="fixture-seo-category-list">
          {MENU_CATEGORIES.map((cat) => (
            <li key={cat.value}>
              <Link to={`/streams/${cat.value}`}>{cat.label} Streams</Link>
            </li>
          ))}
        </ul>
      </nav>
    </section>
  );
});

const TeamImage = memo(function TeamImage({
  src,
  alt,
  size,
}: {
  src?: string;
  alt?: string;
  size: 'desktop' | 'mobile';
}) {
  if (!src) return null;
  if (size === 'desktop') {
    return (
      <img
        src={ `${IMAGE_PROXY_URL}${src}` }
        alt={alt || ''}
        style={styles.imgWidth}
        width={80}
        loading="eager"
        decoding="async"
      />
    );
  }
  return (
    <img
      style={{ textAlign: 'center' }}
      src={ `${IMAGE_PROXY_URL}${src}` }
      alt={alt || ''}
      width={40}
      loading="eager"
      decoding="async"
    />
  );
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function Fixture() {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { teams } = useParams<{ teams: string }>();

  const cacheKey = teams ? fixtureDetailCacheKey(teams) : '';
  const cachedEntry = teams
    ? readSessionCache<{ data?: FixtureData }>(cacheKey)
    : null;
  const cachedFixture = cachedEntry?.data?.data?.game
    ? (cachedEntry.data.data as FixtureData)
    : null;

  const [fixture, setFixture] = useState<FixtureData | null>(cachedFixture);
  const [noFixture, setNoFixture] = useState(false);
  const [loader, setLoader] = useState(!cachedFixture);
  const [fetchError, setFetchError] = useState(false);
  const fetchIdRef = useRef(0);

  const streams = useMemo(
    () => fixture?.game?.streamerLinks?.slice(2) ?? [],
    [fixture],
  );

  const hasStreams =
    (fixture?.game?.streamerLinks?.length ?? 0) > 0 || !!fixture?.game?.websiteLink;

  const localKickOff = useMemo(
    () => formatLocalMatchDate(fixture?.game?.matchDate),
    [fixture?.game?.matchDate],
  );

  const matchPhase = useMemo(
    () => getStaticMatchPhase(fixture?.game?.matchDate),
    [fixture?.game?.matchDate],
  );

  const getFixture = useCallback(async (force = false) => {
    if (!teams) return;

    const requestId = ++fetchIdRef.current;
    const key = fixtureDetailCacheKey(teams);
    const existing = readSessionCache<{ data?: FixtureData }>(key);
    const hasCachedGame = Boolean(existing?.data?.data?.game);

    if (!hasCachedGame) {
      setLoader(true);
    }

    let teamA = teams;
    let teamB = '';
    if (teamA.includes('-vs-')) {
      [teamA, teamB] = teamA.split('-vs-');
    }

    const result = await resilientFetch<{ data?: FixtureData }>({
      key,
      fetcher: () => getFixtureByName({ teamA, teamB }),
      isSuccess: isFixtureDetailResponse,
      skipIfFresh: !force,
      staggerMs: force ? 0 : undefined,
    });

    if (requestId !== fetchIdRef.current) return;

    const data = result.data?.data;

    if (result.data && data?.game) {
      setNoFixture(false);
      setFixture(data as FixtureData);
      setFetchError(result.error);
    } else if (result.data && !result.error) {
      setNoFixture(true);
      setFixture(null);
      setFetchError(false);
    } else if (!hasCachedGame) {
      setNoFixture(true);
      setFixture(null);
      setFetchError(true);
    } else {
      setFetchError(true);
    }

    setLoader(false);
  }, [teams]);

  useEffect(() => {
    if (!teams) return;

    const key = fixtureDetailCacheKey(teams);
    const existing = readSessionCache<{ data?: FixtureData }>(key);
    if (existing?.data?.data?.game) {
      setFixture(existing.data.data as FixtureData);
      setNoFixture(false);
      setLoader(false);
    } else {
      setFixture(null);
      setNoFixture(false);
      setLoader(true);
    }
    setFetchError(false);
    getFixture();
  }, [teams, getFixture]);

  useEffect(() => {
    if (!fixture?.game) return;

    const matchLabel = formatMatchLabel(fixture.game.teamA, fixture.game.teamB);
    const league = fixture.subCategoryName || 'Sports';
    const title = `Watch ${matchLabel} Live Stream Free – ${league} | ${SITE_NAME}`;
    const description = `Watch ${matchLabel} live stream free in HD on ${SITE_NAME}. ${league} coverage with real-time updates, multiple streamer links, and free access — no signup required.`;
    const path = `/fixture/${teams}`;
    const canonical = `${SITE_URL}${path}`;
    const schemaId = 'fixture-page-schema';

    return applyPageSeo({
      title,
      description,
      path,
      keywords: `${matchLabel}, ${league} live stream, free sports stream, ${SITE_NAME}, buffstream.st`,
      schemaId,
      schema: {
        '@context': 'https://schema.org',
        '@type': 'SportsEvent',
        name: matchLabel,
        description,
        startDate: fixture.game.matchDate,
        eventStatus: 'https://schema.org/EventScheduled',
        location: {
          '@type': 'VirtualLocation',
          url: canonical,
        },
        organizer: organizationSchema(),
        ...(fixture.game.teamB
          ? {
              competitor: [
                { '@type': 'SportsTeam', name: fixture.game.teamA },
                { '@type': 'SportsTeam', name: fixture.game.teamB },
              ],
            }
          : {}),
      },
    });
  }, [fixture, teams]);

  const noFixtureNotice = noFixture && (
    <Row style={{ display: 'flex', justifyContent: 'center' }}>
      <Col span={16}>
        <Title level={3}>
          The Match Between {teams} Is No More Available! Please refer to{' '}
          <Link to={HOME_PATH}>
            <Button style={styles.homeButton}>HOME</Button>
          </Link>{' '}
          Page for Today&apos;s Fixtures!
        </Title>
      </Col>
    </Row>
  );

  const linksComingSoonBanner = !hasStreams && matchPhase !== 'FULL TIME' && (
    <TypographyTitle
      style={{
        display: 'flex',
        justifyContent: 'space-around',
        textAlign: 'center',
        background: 'rgb(46 44 44 / 68%)',
      }}
      level={5}
    >
      <p style={{ color: 'white' }}>
        LINKS WILL BE AVAILABLE 1 HOUR BEFORE THE MATCH STARTS, STAY TUNED!
      </p>
    </TypographyTitle>
  );

  const kickOffDisplay = localKickOff || fixture?.game?.matchTime || '';

  return (
    <div className="fixture-page">
      <div className="fixture-shell" style={{ paddingTop: 12 }}>
        <JoinDiscord />
        <SportCategoriesStrip />
      </div>

      <ConnectionBanner
        visible={fetchError && !loader}
        onRetry={() => getFixture(true)}
      />

      {isDesktop ? (
        <div className="fixture-hero">
          {noFixtureNotice}

          <div className="fixture-shell fixture-match-meta">
            <Row>
              <Col span={6}>
                <TeamImage
                  src={fixture?.game?.teamAImage}
                  alt={fixture?.game?.teamA}
                  size="desktop"
                />
                <p style={{ color: 'white' }}>
                  {fixture?.game?.teamB ? fixture.game.teamA : ''}
                </p>
              </Col>

              <Col span={12}>
                <TypographyTitle level={3}>
                  <p className="category-name">{fixture?.subCategoryName}</p>
                </TypographyTitle>

                {!fixture?.game?.teamB && (
                  <TypographyTitle level={5}>
                    <p style={{ color: 'white' }}>{fixture?.game?.teamA}</p>
                  </TypographyTitle>
                )}

                {kickOffDisplay && (
                  <TypographyTitle level={5}>
                    <p style={{ color: '#ffffff', fontSize: '13px' }}>{kickOffDisplay}</p>
                  </TypographyTitle>
                )}
              </Col>

              <Col span={6}>
                <TeamImage
                  src={fixture?.game?.teamBImage}
                  alt={fixture?.game?.teamB}
                  size="desktop"
                />
                <p style={{ color: 'white' }}>{fixture?.game?.teamB}</p>
              </Col>
            </Row>

            {linksComingSoonBanner}
          </div>

          <Row style={styles.flexDisplay}>
            <Col span={16} style={{ marginBottom: '5px' }}>
              {hasStreams && <Player fixture={fixture} />}
            </Col>
          </Row>
        </div>
      ) : (
        <div className="fixture-hero-mobile">
          {noFixtureNotice}

          <div className="fixture-shell fixture-match-meta">
            <TypographyTitle level={3}>
              <p className="category-name">{fixture?.subCategoryName}</p>
            </TypographyTitle>

            <Row
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Col>
                <Row>
                  <TeamImage
                    src={fixture?.game?.teamAImage}
                    alt={fixture?.game?.teamA}
                    size="mobile"
                  />
                </Row>
                <Row style={{ color: 'white' }}>
                  <p>{fixture?.game?.teamB ? fixture.game.teamA : ''}</p>
                </Row>
              </Col>

              <Col>
                {kickOffDisplay && (
                  <TypographyTitle level={5}>
                    <p style={{ color: '#ffffff', marginBottom: '10px', fontSize: '10px' }}>
                      {kickOffDisplay}
                    </p>
                  </TypographyTitle>
                )}
                <Row>
                  <p style={{ color: 'white' }}>
                    {!fixture?.game?.teamB ? fixture?.game?.teamA : ''}
                  </p>
                </Row>
              </Col>

              <Col>
                <Row>
                  <TeamImage
                    src={fixture?.game?.teamBImage}
                    alt={fixture?.game?.teamB}
                    size="mobile"
                  />
                </Row>
                <Row>
                  <p style={{ color: 'white' }}>{fixture?.game?.teamB}</p>
                </Row>
              </Col>
            </Row>

            {linksComingSoonBanner}
          </div>

          {hasStreams && <Player fixture={fixture} />}
        </div>
      )}

      {streams.length > 0 && (
        <div className="fixture-shell">
          <StreamsList streams={streams} isDesktop={isDesktop} />
        </div>
      )}

      {!loader && !noFixture && fixture?.game && (
        <div className="fixture-shell">
          <FixtureDescription
            fixture={fixture}
            matchPhase={matchPhase}
            localKickOff={localKickOff}
          />
        </div>
      )}

      {loader && <Loader />}
    </div>
  );
}
